import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { AccessService, BotAccessState } from '../../../../services/access.service';
import { Tutor, TutorService } from '../../../../services/tutor.service';
import { User, UserService } from '../../../../services/user.service';

interface AccessRow {
  user: User;
  hasAccess: boolean;
  isOwner: boolean;
  isCreator: boolean;
  /** True while a grant/revoke request for this row is in flight — the toggle
   *  is disabled so a double-click can't fire two conflicting writes. */
  busy: boolean;
}

/**
 * Bot-edit sub-page: manage which users may see and use this agent.
 *
 * The route is only reachable by admin / manager / super_admin (the tutor-menu
 * hides the entry otherwise and the backend rejects the grant endpoints for
 * everyone else), so this is a management surface, not a self-service one.
 *
 * Access here means "may see and use" — there is no view/edit distinction, the
 * backend never enforced one. Owner and creator rows are shown for context:
 * the owner grant is not revocable (it is the agent's responsible person), and
 * the creator is badged as a historical fact even when they hold no grant.
 */
@Component({
  selector: 'app-access',
  templateUrl: './access.component.html',
  styleUrl: './access.component.scss',
})
export class AccessComponent implements OnInit, OnDestroy {
  botId!: string;
  bot: Tutor | undefined;

  rows: AccessRow[] = [];
  loading = true;
  search = '';

  page = 0;
  pageSize = 25;
  readonly pageSizeOptions = [25, 50, 100];

  private access: BotAccessState | undefined;
  private destroy$ = new Subject<void>();

  constructor(
    private activeRoute: ActivatedRoute,
    private accessService: AccessService,
    private userService: UserService,
    private tutorService: TutorService,
    private toastr: NbToastrService,
    private t: TranslateService,
  ) {}

  ngOnInit(): void {
    this.botId = this.activeRoute.parent!.snapshot.paramMap.get('tutorId')!;

    this.tutorService.tutors$.pipe(takeUntil(this.destroy$)).subscribe((all) => {
      this.bot = all.find((b) => b.id === this.botId);
    });
    this.tutorService.listTutors().subscribe();

    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get filteredRows(): AccessRow[] {
    const q = this.search.trim().toLowerCase();
    if (!q) return this.rows;
    return this.rows.filter(
      (r) =>
        r.user.name?.toLowerCase().includes(q) ||
        r.user.email?.toLowerCase().includes(q),
    );
  }

  /** Current page of the filtered rows — the list renders this, the pagination
   *  control drives `page` / `pageSize`. */
  get pagedRows(): AccessRow[] {
    const start = this.page * this.pageSize;
    return this.filteredRows.slice(start, start + this.pageSize);
  }

  onSearchChange(): void {
    // Any filter change can shrink the list below the current page — reset so
    // the user isn't stranded on an empty page.
    this.page = 0;
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.page = 0;
  }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      const [users, access] = await Promise.all([
        firstValueFrom(this.userService.listUsers()),
        this.accessService.getBotAccess(this.botId),
      ]);
      this.access = access;
      const granted = new Set(access.user_ids);
      const owners = new Set(access.owner_user_ids);
      this.rows = users
        // Learners are not client users; the /users list is already the right
        // population. Sort granted first, then by name, so the current state
        // reads at a glance.
        .map((user) => ({
          user,
          hasAccess: granted.has(user.id),
          isOwner: owners.has(user.id),
          isCreator: access.created_by_user_id === user.id,
          busy: false,
        }))
        .sort(
          (a, b) =>
            Number(b.hasAccess) - Number(a.hasAccess) ||
            (a.user.name || '').localeCompare(b.user.name || ''),
        );
    } catch {
      this.toastr.danger(
        this.t.instant('pages.tutors.access.loadError'),
        this.t.instant('toasts.error'),
      );
    } finally {
      this.loading = false;
    }
  }

  async toggle(row: AccessRow): Promise<void> {
    if (row.busy || row.isOwner) return;
    const next = !row.hasAccess;
    row.busy = true;
    // Optimistic: flip immediately, revert on failure so the toggle never
    // shows a state the server didn't accept.
    row.hasAccess = next;
    try {
      if (next) {
        await this.accessService.grantBot(row.user.id, this.botId);
      } else {
        await this.accessService.revokeBot(row.user.id, this.botId);
      }
    } catch {
      row.hasAccess = !next;
      this.toastr.danger(
        this.t.instant('pages.tutors.access.saveError'),
        this.t.instant('toasts.error'),
      );
    } finally {
      row.busy = false;
    }
  }
}
